/**
 * © [2020] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import React from 'react';
import { useSelector } from 'react-redux';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import _ from 'lodash';
import Spinner from '../../../../SharedModules/Spinner/SpinnerNew';
import SelectBoxWithoutBoxComponent from '../../../../SharedModules/SelectBox/SelectBoxWithoutBox';
import PDFExportPanel from '../../../../SharedModules/pdfExport/PDFExportPanel';
import { convertPDFURLFromAPI } from '../../../../SharedModules/CommonUtilities/commonUtilities';
import { safeWindowOpen } from '../../../../SharedModules/CommonUtilities/safeWindowOpen';

function DisplayLetterPreview() {
  const previewTemplate = useSelector(
    (state) => state.sharedState.LetterTemplateReducer.previewTemplate
  );
  const pdfPreview = useSelector(
    (state) => state.sharedState.LetterTemplateReducer.pdfPreview
  );
  const letterTemplateDetailPage = useSelector(
    (state) =>
      state.sharedState.LetterTemplateReducer.letterTemplateDetail.searchValue
  );
  const newpostdata = useSelector(
    (state) => state.sharedState.LetterTemplateReducer.newpostdata
);
  const [loading, setLoading] = React.useState(true);
  const [pTData, setPTDate] = React.useState(null);
  const [pdfUrl, setPdfURL] = React.useState(null);
  // null = default; 'main' = Show Main; 'headerFooter' = Show Header and Footer
  const [previewMode, setPreviewMode] = React.useState(null);
  // Bumped on every Show Main / Show Header and Footer click so the PDF
  // viewer remounts and reapplies zoom after the user zooms out (Ctrl +/- / scroll).
  const [viewerNonce, setViewerNonce] = React.useState(0);

  const isZoomed = previewMode === 'main' || previewMode === 'headerFooter';

  React.useEffect(() => {
    if (previewTemplate && !_.isEmpty(previewTemplate)) {
      setPTDate(previewTemplate);
    }
  }, [previewTemplate]);

  React.useEffect(() => {
    if (pdfPreview && pdfPreview !== '') {
      const purl = convertPDFURLFromAPI(pdfPreview);
      setPdfURL((previousUrl) => {
        if (previousUrl && previousUrl.startsWith('blob:')) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return purl;
      });
      setLoading(false);
    } else {
      setLoading(false);
    }
    // Recreate blob when zoom toggles or the same zoom action is clicked
    // again so the PDF viewer applies the hash (Chrome ignores hash-only updates).
  }, [pdfPreview, isZoomed, viewerNonce]);

  React.useEffect(
    () => () => {
      if (pdfUrl && pdfUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    },
    [pdfUrl]
  );

  const onClickPrint = () => {
    safeWindowOpen(pdfUrl);
  };

  // Stay on Display Letter Preview — do not navigate to Letter Detail
  const onClickMainHeaderFooter = (action) => {
    setPreviewMode(action === 'HeaderFooter' ? 'headerFooter' : 'main');
    setViewerNonce((nonce) => nonce + 1);
  };

  const pdfEmbedSrc = React.useMemo(() => {
    if (!pdfUrl) {
      return '';
    }
    if (isZoomed) {
      return `${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=150&v=${viewerNonce}`;
    }
    return `${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1`;
  }, [pdfUrl, isZoomed, viewerNonce]);

  const embedKey = isZoomed
    ? `letter-preview-zoomed-${viewerNonce}`
    : 'letter-preview-default';

  return (
    <div
      className="lettterTemplateDetail p-0"
      data-testid="letter-template-preview-component"
    >
      <Spinner load={loading} />
      <div className="tabs-container">
        <div className="mui-custom-form no-margin custom-check">
          <fieldset
            data-testid="radio-box-component"
            className="custom-fieldset"
          >
            <div className="row">
              <div className="col-3 mb-0">
                <SelectBoxWithoutBoxComponent
                  data-testid="letter-id-input-box"
                  labelName="Letter ID"
                  required={false}
                  disabled
                  select={false}
                />
              </div>

              <div className="col-3 mb-0">
                <SelectBoxWithoutBoxComponent
                  data-testid="name-input-box"
                  labelName="Name"
                  required={false}
                  disabled
                  select={false}
                  defaultValue={pTData?.mfgName}
                />
              </div>

              <div className="col-3 mb-0">
                <SelectBoxWithoutBoxComponent
                  data-testid="mfg-id-input-box"
                  labelName="Mfg ID"
                  required={false}
                  disabled
                  select={false}
                  defaultValue={pTData?.mfgId}
                />
              </div>

              <div className="col-3 mb-0">
                <SelectBoxWithoutBoxComponent
                  data-testid="Sent-input-box"
                  labelName="Sent"
                  required={false}
                  disabled
                  select={false}
                  className={
                    newpostdata?.letterTemplate?.sentDt === '' ||
                    newpostdata?.letterTemplate?.sentDt == null
                      ? 'frr-letter-used-indicator'
                      : ''
                  }
                />
              </div>
            </div>
          </fieldset>
        </div>

        <div className="custom-check">
          <div className="float-right mt-2">
            <div className="panel-header">
              <div className="float-right th-icon-group mt-3">
                <div className="panel-header">
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip id="history-tooltip"> Actions </Tooltip>}
                  >
                    <div className="th-icon ic-actions">
                      <PDFExportPanel
                        data-testid="letter-template-preview-action"
                        isExport={false}
                        addRowsView
                        addRowsTitle="Print"
                        onClickAddNdC={onClickPrint}
                        viewCopyPage="Show Main"
                        viewCopyPageAction={() =>
                          onClickMainHeaderFooter('Body')
                        }
                        viewPreview="Show Header and Footer"
                        viewPreviewAction={() =>
                          onClickMainHeaderFooter('HeaderFooter')
                        }
                      />
                    </div>
                  </OverlayTrigger>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="clearfix devider-line" />
        {pdfUrl && pdfUrl !== '' ? (
          <div
            className={`frr-letter-preview-container${
              isZoomed ? ' frr-letter-preview-zoomed' : ''
            }`}
            data-testid="letter-preview-container"
          >
            <embed
              key={embedKey}
              src={pdfEmbedSrc}
              type="application/pdf"
              frameBorder="0"
              scrolling="auto"
              height="500px"
              width="100%"
              className="frr-letter-preview-embed"
              data-testid="letter-preview-embed"
            />
          </div>
        ) : (
          ''
        )}
      </div>
    </div>
  );
}

export default DisplayLetterPreview;
